import type { ReactNode } from 'react';
import type { Membership } from '../../../lib/api';

const DEFAULT_CAPTION = (
  <>
    Rows = <strong>receiver tier</strong> (the direct sponsor earning the
    bonus). Columns = <strong>buyer tier</strong> (the new slot's tier).
    Cell = flat amount paid to the sponsor at slot activation. Blank /
    zero = no payout for that pair.
  </>
);

/**
 * Per-tier flat-amount matrix editor (Phase 12B). The plan reads
 *   memberships[receiverTier].config.computation[configKey][buyerTierCode]
 * to find the FLAT AMOUNT (not %) that receiver earns when a slot of
 * `buyerTierCode` is activated below them.
 *
 * UI is a 2D matrix: rows = receiver tier (one membership row each),
 * columns = buyer tier, cells = amount. Editing a cell deep-merges the
 * receiver's config row.
 *
 * Defaults to the `direct` plan. Pass `configKey`/`caption` to reuse it for
 * any other earner×buyer flat-amount mechanism (e.g. `australian_pass_up`).
 */
export function DirectMatrixPerTier({
  memberships,
  draft,
  onChange,
  readOnly,
  configKey = 'direct',
  caption = DEFAULT_CAPTION,
}: {
  memberships: Membership[];
  draft: Record<string, Record<string, unknown>>;
  onChange: (id: string, next: Record<string, unknown>) => void;
  readOnly: boolean;
  /** Which `computation.*` sub-block to read/write. Defaults to `direct`. */
  configKey?: string;
  /** Description shown above the matrix. */
  caption?: ReactNode;
}) {
  function getCell(receiverCfg: Record<string, unknown>, buyerCode: string): number {
    const computation = (receiverCfg.computation as Record<string, unknown> | undefined) ?? {};
    const block = (computation[configKey] as Record<string, unknown> | undefined) ?? {};
    const v = block[buyerCode];
    return typeof v === 'number' || typeof v === 'string' ? Number(v) || 0 : 0;
  }

  function setCell(membershipId: string, buyerCode: string, n: number) {
    const current = draft[membershipId] ?? {};
    const computation = ((current.computation as Record<string, unknown>) ?? {}) as Record<
      string,
      unknown
    >;
    const block = {
      ...((computation[configKey] as Record<string, unknown> | undefined) ?? {}),
    } as Record<string, unknown>;
    if (n === 0) {
      delete block[buyerCode];
    } else {
      block[buyerCode] = n;
    }
    const nextComputation = { ...computation };
    if (Object.keys(block).length === 0) delete nextComputation[configKey];
    else nextComputation[configKey] = block;

    const nextCfg = { ...current };
    if (Object.keys(nextComputation).length === 0) delete nextCfg.computation;
    else nextCfg.computation = nextComputation;
    onChange(membershipId, nextCfg);
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-muted-foreground">{caption}</p>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/40 text-xs text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium sticky left-0 bg-muted/40">
                Receiver \ Buyer
              </th>
              {memberships.map((m) => (
                <th key={m.code} className="px-3 py-2 font-mono font-medium" title={m.name}>
                  {m.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {memberships.map((receiver) => (
              <tr key={receiver.id} className="border-t">
                <td
                  className="px-3 py-1.5 text-xs font-mono font-medium bg-muted/20 sticky left-0"
                  title={receiver.name}
                >
                  {receiver.code}
                </td>
                {memberships.map((buyer) => {
                  const cell = getCell(draft[receiver.id] ?? {}, buyer.code);
                  return (
                    <td key={buyer.code} className="px-2 py-1.5">
                      <input
                        type="number"
                        className="h-8 w-20 px-2 rounded-md border bg-background text-xs disabled:opacity-50"
                        value={cell}
                        disabled={readOnly}
                        step={0.01}
                        min={0}
                        onChange={(e) =>
                          setCell(receiver.id, buyer.code, Number(e.target.value) || 0)
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
